import React, { useState, useContext, useEffect, useRef } from "react";
import clsx from "clsx";
import {
  makeStyles,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  MenuItem,
  IconButton,
  Menu,
  Switch,
  Box,
  useMediaQuery,
  useTheme,
  Button,
  Tooltip,
} from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import AccountCircle from "@material-ui/icons/AccountCircle";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import LockIcon from "@material-ui/icons/Lock";
import LockOpenIcon from "@material-ui/icons/LockOpen";

import MainListItems from "./MainListItems";
import { useWhiteLabel, getWLLogo } from "../context/WhiteLabel/WhiteLabelContext";
import NotificationsPopOver from "../components/NotificationsPopOver";
import NotificationBell from "../components/NotificationBell";
import NotificationSettingsButton from "../components/NotificationSettingsButton";
import AppBanner from "../components/AppBanner";
import MatchPropBanner from "../components/MatchPropBanner";
import { ChatHighlightProvider } from "../context/ChatHighlight/ChatHighlightContext";
import { BannerActiveProvider, useBannerActive } from "../context/BannerActive/BannerActiveContext";
import UserModal from "../components/UserModal";
import ViewAsUserModal from "../components/ViewAsUserModal";
import api from "../services/api";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import { useThemeContext } from "../context/DarkMode";
import { useUiPrefs } from "../context/UiPrefs";
import FloatingChat from "../components/FloatingChat";
import useWhatsApps from "../hooks/useWhatsApps";
import useHeartbeat from "../hooks/useHeartbeat";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { getSocket } from "../services/socket-io";

// feat_328: `drawerWidth` ya no es constante de módulo — sale del contexto de
// densidad y entra a makeStyles como prop (patrón de props de MUI v4).
const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    position: "relative",
    [theme.breakpoints.down("sm")]: {
      height: "100vh",
    },
  },
  toolbar: {
    paddingRight: 24, // keep right padding when drawer closed
  },
  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: "0 8px",
    minHeight: "48px",
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    backgroundColor: theme.palette.background.default,
  },
  appBarImpersonating: {
    top: 36,
    [theme.breakpoints.down("xs")]: { top: 30 },
  },
  appBarShift: {
    marginLeft: (props) => props.drawerWidth,
    width: (props) => `calc(100% - ${props.drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: 36,
    color: theme.palette.text.primary,
  },
  menuButtonHidden: {
    display: "none",
  },
  title: {
    flexGrow: 1,
    color: theme.palette.text.primary,
    minWidth: 0, // permite encogerse por debajo del contenido (flex auto = no shrink, MUI v4 default)
    overflow: "hidden", // defensivo: si el banner desborda, no empuja los íconos derechos fuera de viewport
  },
  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: (props) => props.drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    backgroundColor: theme.palette.background.paper,
  },
  drawerPaperClose: {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9),
    },
  },
  appBarSpacer: {
    minHeight: "48px",
  },
  content: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "auto",
  },
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
  },
  switch: {
    transform: "scale(0.8)",
  },
  iconButton: {
    color: theme.palette.text.primary,
  },
  themeSwitchContainer: {
    display: "flex",
    alignItems: "center",
  },
  themeIcon: {
    color: theme.palette.text.primary,
  },
  userName: {
    color: theme.palette.text.primary,
    fontSize: 13,
    fontWeight: 500,
    marginRight: 2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 160,
  },
  drawerHeaderDivider: {
    height: 32,
    margin: theme.spacing(0, 1),
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)",
    width: 1,
  },
}));

// feat_137: métricas del header. En <1280px se ocultan con fade mientras el
// AppBanner tiene un item activo (marquee), reaparecen al quedar idle.
const HeaderMetrics = ({ summaryStats, theme }) => {
  const { isBannerActive } = useBannerActive();
  const isBelowLg = useMediaQuery(theme.breakpoints.down("md")); // <1280px en MUI v4
  const shouldHide = isBelowLg && isBannerActive;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontSize: 11,
        color: theme.palette.text.secondary,
        marginRight: shouldHide ? 0 : 12,
        whiteSpace: "nowrap",
        flexShrink: 0,
        opacity: shouldHide ? 0 : 1,
        visibility: shouldHide ? "hidden" : "visible",
        maxWidth: shouldHide ? 0 : "none",
        overflow: "hidden",
        transition: "opacity 200ms ease, max-width 200ms ease, margin-right 200ms ease",
        pointerEvents: shouldHide ? "none" : "auto",
      }}
    >
      <Tooltip title={i18n.t("headerMetrics.resueltos")} arrow>
        <span>✅ {summaryStats.ticketsResolved} resueltos</span>
      </Tooltip>
      <span style={{ opacity: 0.35 }}>|</span>
      <Tooltip title={i18n.t("headerMetrics.atencionEspera")} arrow>
        <span>👥 {summaryStats.ticketsOpen ?? "—"} atención / {summaryStats.ticketsPending ?? "—"} espera</span>
      </Tooltip>
      <span style={{ opacity: 0.35 }}>|</span>
      <Tooltip title={i18n.t("headerMetrics.tdr1ra")} arrow>
        <span>⏱ TDR 1ra: {summaryStats.avgFirstResponseMinutes != null ? `${summaryStats.avgFirstResponseMinutes}m` : "—"}</span>
      </Tooltip>
      <span style={{ opacity: 0.35 }}>|</span>
      <Tooltip title={i18n.t("headerMetrics.tdrResp")} arrow>
        <span>📊 TDR resp: {summaryStats.avgResponseMinutes != null ? `${summaryStats.avgResponseMinutes}m` : "—"}</span>
      </Tooltip>
    </div>
  );
};

const LoggedInLayout = ({ children }) => {
  // feat_328 — densidad visual: el contexto se lee ANTES de useStyles porque
  // `drawerWidth` viaja como prop del hook de estilos.
  const { darkMode, toggleTheme, density, setDensityValue, cfg } = useThemeContext();
  const drawerWidth = cfg?.drawerWidth || 240;
  const classes = useStyles({ drawerWidth });
  const theme = useTheme();
  useHeartbeat();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading } = useContext(AuthContext);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerVariant, setDrawerVariant] = useState("permanent");
  const [sidebarPinned, setSidebarPinned] = useState(() => {
    const stored = localStorage.getItem("sidebarPinned");
    return stored === null ? true : stored === "true";
  });
  const hoverCloseTimerRef = useRef(null);
  // feat_299 — `user` puede ser el target del modo "Ver como usuario";
  // `realUser` es SIEMPRE el admin logueado (gate del menú + banner).
  const { user, realUser, viewAsUser, activateViewAs, exitViewAs } = useContext(AuthContext);
  const [viewAsDialogOpen, setViewAsDialogOpen] = useState(false);
  const { showGroups, hideChannels, toggleShowGroups, toggleHideChannels } = useUiPrefs();
  const { whiteLabel } = useWhiteLabel();
  const history = useHistory();

  // IA control switches in profile menu (feat_181) — admin only, gated by iaAdminCanToggle
  const [iaAdminCanToggle, setIaAdminCanToggle] = useState(false);
  const [secretariaIaEnabled, setSecretariaIaEnabled] = useState(true);
  const [vendedorIaEnabled, setVendedorIaEnabled] = useState(true);
  // feat_288 (Fase B): operative ON/OFF layer — the toolbar/profile switches
  // toggle these (secretariaIaActive/vendedorIaActive), NOT the *Enabled config.
  const [secretariaIaActive, setSecretariaIaActive] = useState(true);
  const [vendedorIaActive, setVendedorIaActive] = useState(true);
  // todo_097 — el toggle «🤖 IA» de acá es el de la SECRETARIA/VENDEDOR (bot de la
  // empresa). Una línea que resuelve por `turnos_line` no lo mira: su gate es
  // `turnos_db.Companies.iaEnabled`, que se prende y apaga en TurnosIA › Ajustes.
  // Si TODAS las líneas de la empresa son de TurnosIA (caso ITV Venado Tuerto),
  // este control no hace absolutamente nada y mostrarlo en verde con el asistente
  // apagado es una sesión de debugging esperando a pasar → se OCULTA.
  // Si hay líneas de los dos tipos (caso Noale), el toggle SIGUE sirviendo para las
  // otras: no se toca, solo se aclara en el tooltip a quién gobierna.
  // ⛔ Cero cambio de comportamiento: `secretariaIaActive` no se lee ni se escribe
  // distinto, y quien no tiene línea de TurnosIA ve exactamente lo de siempre.
  const [turnosLines, setTurnosLines] = useState({ total: 0, turnos: 0 });
  const iaToolbarInert = turnosLines.total > 0 && turnosLines.turnos === turnosLines.total;
  const iaToolbarMixed = turnosLines.turnos > 0 && !iaToolbarInert;
  // Only show the operative switch when IA is CONFIGURED (secretariaIaEnabled).
  const showIaMenu =
    user?.profile === "admin" && !user?.isSuperAdmin && iaAdminCanToggle && secretariaIaEnabled && !iaToolbarInert;

  // WhatsApp disconnection banner — only for company users (not superadmin)
  const { whatsApps } = useWhatsApps();
  const disconnectedWhatsApps = user?.isSuperAdmin
    ? []
    : whatsApps.filter(w =>
        w.status === "DISCONNECTED" ||
        w.status === "qrcode" ||
        w.status === "PAIRING"
      );
  const whatsAppBannerHeight = disconnectedWhatsApps.length > 0 ? (isSmall ? 44 : 48) : 0;

  // Switch de envío por conexión: una conexión con los envíos pausados es un
  // estado peligroso de olvidar → banner fijo en TODA la app, para todo perfil
  // de la empresa (sus mensajes no salen). Superadmin afuera, igual que arriba.
  const pausedWhatsApps = user?.isSuperAdmin
    ? []
    : whatsApps.filter(w => w.outboundPaused);
  const pausedBannerHeight = pausedWhatsApps.length > 0 ? (isSmall ? 36 : 40) : 0;

  const getDisplayName = () => {
    if (!user?.name) return "";
    if (isSmall) {
      // Iniciales para el avatar mini en mobile — NO tocar, uso distinto del
      // nombre completo de abajo (ver fix-sobreturno-fecha-nombre).
      return user.name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
    }
    return user.name;
  };

  const [summaryStats, setSummaryStats] = useState(null);

  useEffect(() => {
    if (!user?.id || user.isSuperAdmin) return;
    const fetchStats = () => {
      api.get("/stats/summary").then(({ data }) => setSummaryStats(data)).catch(() => {});
    };
    fetchStats();
    const interval = setInterval(fetchStats, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user?.id]);

  const impersonatingCompany = localStorage.getItem("impersonating_company");

  const handleStopImpersonating = async () => {
    const backupToken = localStorage.getItem("token_superadmin_backup");
    if (backupToken) {
      try {
        // Use unauthenticated endpoint — passes backup token in body so backend
        // can verify isSuperAdmin and restore the jrt cookie without needing
        // the current (impersonated) Authorization header to be valid.
        // backupToken is stored as a raw JWT string (not JSON-stringified).
        const { data } = await api.post("/auth/stop-impersonation", {
          backupToken
        });
        if (data?.token) {
          localStorage.setItem("token", JSON.stringify(data.token));
        }
        // feat_248: regenerar wl_refresh_token con la identidad del super. Durante
        // la impersonation el mount/interceptor pudieron pisarlo con el refresh del
        // admin impersonado (bug pre-fix) → al reload, el mount lo usaba y re-hidrataba
        // como el admin (menú de empresa) con el token del super activo = estado mixto.
        if (data?.refreshToken) {
          localStorage.setItem("wl_refresh_token", data.refreshToken);
        }
      } catch (_) {}
    }
    // feat_247: limpiar el estado de impersonation SIEMPRE (fuera del if backupToken).
    // Si impersonating_refresh_token sobrevive, el mount effect de useAuth y el
    // interceptor 403 (feat_246) lo PRIORIZAN → tras volver a super, la sesión se
    // re-hidrata como el admin impersonado (menú de admin) mientras el token en
    // localStorage ya es el del super (datos cross-tenant) → estado MIXTO reportado.
    localStorage.removeItem("token_superadmin_backup");
    localStorage.removeItem("impersonating_company");
    localStorage.removeItem("impersonating_refresh_token");
    window.location.href = "/superadmin";
  };

  useEffect(() => {
    if (document.body.offsetWidth > 600 && sidebarPinned) {
      setDrawerOpen(true);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("sidebarPinned", String(sidebarPinned));
  }, [sidebarPinned]);

  useEffect(() => {
    if (!sidebarPinned && drawerVariant === "permanent") {
      setDrawerOpen(false);
    }
  }, [sidebarPinned, drawerVariant]);

  useEffect(() => {
    return () => {
      if (hoverCloseTimerRef.current) clearTimeout(hoverCloseTimerRef.current);
    };
  }, []);

  const handleDrawerMouseEnter = () => {
    if (sidebarPinned || drawerVariant !== "permanent") return;
    if (hoverCloseTimerRef.current) {
      clearTimeout(hoverCloseTimerRef.current);
      hoverCloseTimerRef.current = null;
    }
    setDrawerOpen(true);
  };

  const handleDrawerMouseLeave = () => {
    if (sidebarPinned || drawerVariant !== "permanent") return;
    hoverCloseTimerRef.current = setTimeout(() => {
      setDrawerOpen(false);
      hoverCloseTimerRef.current = null;
    }, 600);
  };

  useEffect(() => {
    if (document.body.offsetWidth < 600) {
      setDrawerVariant("temporary");
    } else {
      setDrawerVariant("permanent");
    }
  }, [drawerOpen]);

  // Notificación global: pendiente vencido
  useEffect(() => {
    const socket = getSocket();
    const handleTicketPending = (data) => {
      if (data.action === "overdue") {
        toast.warn(
          "⏰ Tenés un pendiente vencido — revisá el Tablero de Pendientes",
          {
            toastId: "pending-overdue-" + data.pendingId,
            autoClose: 8000,
            onClick: () => history.push("/pending-dashboard"),
          }
        );
      }
    };
    socket.on("ticketPending", handleTicketPending);
    // SINGLETON: off() selectivo, NUNCA disconnect().
    return () => socket.off("ticketPending", handleTicketPending);
  }, []);

  // feat_181: load IA control settings for the profile-menu switches (admin only) + live socket
  useEffect(() => {
    if (!user?.id || user.isSuperAdmin || user.profile !== "admin") return;
    let active = true;
    api.get("/settings").then(({ data }) => {
      if (!active || !Array.isArray(data)) return;
      const gv = (k) => data.find((s) => s.key === k)?.value;
      setIaAdminCanToggle(gv("iaAdminCanToggle") === "true");
      setSecretariaIaEnabled(gv("secretariaIaEnabled") !== "false");
      setVendedorIaEnabled(gv("vendedorIaEnabled") !== "false");
      setSecretariaIaActive(gv("secretariaIaActive") !== "false");
      setVendedorIaActive(gv("vendedorIaActive") !== "false");
    }).catch(() => {});

    const socket = getSocket();
    const handleSettings = (d) => {
      const { key, value } = d?.setting || {};
      if (key === "iaAdminCanToggle") setIaAdminCanToggle(value === "true");
      if (key === "secretariaIaEnabled") setSecretariaIaEnabled(value === "true");
      if (key === "vendedorIaEnabled") setVendedorIaEnabled(value === "true");
      if (key === "secretariaIaActive") setSecretariaIaActive(value === "true");
      if (key === "vendedorIaActive") setVendedorIaActive(value === "true");
    };
    socket.on("settings", handleSettings);
    // SINGLETON: off() selectivo, NUNCA disconnect().
    return () => { active = false; socket.off("settings", handleSettings); };
  }, [user?.id, user?.isSuperAdmin, user?.profile]);

  // todo_097 — cuántas líneas de la empresa las atiende TurnosIA. Solo lectura, y
  // solo para el admin que además puede ver el toggle: una empresa sin él no gasta
  // la llamada. FAIL-SAFE: si falla queda {0,0} → el toggle se ve como siempre
  // (nunca se oculta un control por un error de red).
  useEffect(() => {
    if (!user?.id || user.isSuperAdmin || user.profile !== "admin" || !iaAdminCanToggle) return;
    let active = true;
    api.get("/whatsapp/lines-status").then(({ data }) => {
      if (!active || !Array.isArray(data)) return;
      setTurnosLines({
        total: data.length,
        turnos: data.filter((l) => l?.nivelResuelto === "turnos_line").length,
      });
    }).catch(() => {});
    return () => { active = false; };
  }, [user?.id, user?.isSuperAdmin, user?.profile, iaAdminCanToggle]);

  // feat_288 (Fase B): operative toggles write *Active. Secretaria off forces
  // Vendedor off (dependency). The *Enabled config is untouched here.
  const handleToggleSecretariaIaMenu = async (checked) => {
    setSecretariaIaActive(checked);
    try {
      if (!checked) {
        setVendedorIaActive(false);
        await Promise.all([
          api.put("/settings/secretariaIaActive", { value: "false" }),
          api.put("/settings/vendedorIaActive", { value: "false" }),
        ]);
      } else {
        await api.put("/settings/secretariaIaActive", { value: "true" });
      }
    } catch (err) {
      setSecretariaIaActive(!checked);
      toast.error(i18n.t("settings.error") || "Error");
    }
  };

  const handleToggleVendedorIaMenu = async (checked) => {
    if (!secretariaIaActive) return;
    setVendedorIaActive(checked);
    try {
      await api.put("/settings/vendedorIaActive", { value: checked ? "true" : "false" });
    } catch (err) {
      setVendedorIaActive(!checked);
      toast.error(i18n.t("settings.error") || "Error");
    }
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  const drawerClose = () => {
    if (document.body.offsetWidth < 600) {
      setDrawerOpen(false);
    }
  };

  if (loading) {
    return <BackdropLoading />;
  }

  const impersonatingHeight = impersonatingCompany ? (isSmall ? 30 : 36) : 0;
  // feat_299 — el banner de "Ver como usuario" apila DEBAJO del de
  // impersonación (naranja) y ARRIBA del de conexiones caídas.
  const viewAsHeight = viewAsUser ? (isSmall ? 30 : 36) : 0;
  const totalTopOffset = impersonatingHeight + viewAsHeight + whatsAppBannerHeight + pausedBannerHeight;

  return (
    <ChatHighlightProvider>
    <BannerActiveProvider>
    <div
      className={classes.root}
      style={totalTopOffset > 0 ? {
        marginTop: totalTopOffset,
        height: `calc(100vh - ${totalTopOffset}px)`,
      } : {}}
    >
      {impersonatingCompany && (
        <div style={{
          position: "fixed",
          top: 0, left: 0, right: 0,
          zIndex: 9999,
          backgroundColor: "#f57c00",
          color: "#fff",
          padding: isSmall ? "3px 8px" : "6px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontSize: isSmall ? 11 : 14,
          fontWeight: "bold",
          height: isSmall ? 30 : 36,
          boxSizing: "border-box",
        }}>
          <span>⚠️ {isSmall ? impersonatingCompany : `Estás viendo como: `}{!isSmall && <strong>{impersonatingCompany}</strong>}</span>
          <button
            onClick={handleStopImpersonating}
            style={{
              background: "rgba(0,0,0,0.2)",
              border: "none",
              color: "#fff",
              padding: isSmall ? "2px 6px" : "4px 12px",
              borderRadius: 4,
              cursor: "pointer",
              fontWeight: "bold",
              fontSize: isSmall ? 10 : 13,
            }}
          >
            {isSmall ? "← Salir" : "Volver a Superadmin"}
          </button>
        </div>
      )}

      {viewAsUser && (
        <div style={{
          position: "fixed",
          top: impersonatingHeight,
          left: 0, right: 0,
          zIndex: 9999,
          backgroundColor: "#3f51b5",
          color: "#fff",
          padding: isSmall ? "3px 8px" : "6px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontSize: isSmall ? 11 : 14,
          fontWeight: "bold",
          height: viewAsHeight,
          boxSizing: "border-box",
          gap: 8,
        }}>
          <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            👁️ {i18n.t("viewAs.banner", { name: viewAsUser.name })}
          </span>
          <button
            onClick={exitViewAs}
            style={{
              background: "rgba(0,0,0,0.2)",
              border: "none",
              color: "#fff",
              padding: isSmall ? "2px 6px" : "4px 12px",
              borderRadius: 4,
              cursor: "pointer",
              fontWeight: "bold",
              fontSize: isSmall ? 10 : 13,
              flexShrink: 0,
            }}
          >
            {i18n.t("viewAs.exit")}
          </button>
        </div>
      )}

      {disconnectedWhatsApps.length > 0 && (
        <div style={{
          position: "fixed",
          top: impersonatingHeight + viewAsHeight,
          left: 0,
          right: 0,
          zIndex: 9998,
          backgroundColor: "#dc2626",
          color: "#fff",
          padding: isSmall ? "4px 8px" : "6px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontSize: isSmall ? 11 : 13,
          fontWeight: "bold",
          height: whatsAppBannerHeight,
          boxSizing: "border-box",
          gap: 8,
        }}>
          <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            ⚠️{" "}
            {disconnectedWhatsApps.map((w, i) => {
              const isMetaConn = w.provider === "meta" || w.provider === "meta_coexistence";
              const statusText = (w.status === "qrcode" || w.status === "PAIRING")
                ? i18n.t("connectionBanner.statusWaitingQr")
                : i18n.t("connectionBanner.statusDisconnected");
              // Meta connections have no QR — never tell the user to scan one.
              const actionText = isMetaConn
                ? i18n.t("connectionBanner.actionMeta")
                : i18n.t("connectionBanner.actionQr");
              return (
                <span key={w.id}>
                  {i > 0 && " · "}
                  {i18n.t("connectionBanner.prefix")} <strong>{w.name}</strong> {statusText}.
                  {" "}{actionText}
                </span>
              );
            })}
          </span>
          <button
            onClick={() => history.push("/connections")}
            style={{
              background: "rgba(0,0,0,0.25)",
              border: "none",
              color: "#fff",
              padding: isSmall ? "2px 8px" : "3px 12px",
              borderRadius: 4,
              cursor: "pointer",
              fontWeight: "bold",
              fontSize: isSmall ? 10 : 12,
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {i18n.t("connectionBanner.goToConnections")}
          </button>
        </div>
      )}

      {pausedWhatsApps.length > 0 && (
        <div style={{
          position: "fixed",
          top: impersonatingHeight + viewAsHeight + whatsAppBannerHeight,
          left: 0,
          right: 0,
          zIndex: 9998,
          // Rayado rojo/negro: distinto del rojo liso de "conexión caída".
          background: "repeating-linear-gradient(45deg, #b91c1c, #b91c1c 14px, #7f1d1d 14px, #7f1d1d 28px)",
          color: "#fff",
          padding: isSmall ? "4px 8px" : "6px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontSize: isSmall ? 11 : 13,
          fontWeight: "bold",
          height: pausedBannerHeight,
          boxSizing: "border-box",
          gap: 8,
        }}>
          <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            ⏸️ {i18n.t("connectionBanner.pausedPrefix")}{" "}
            <strong>{pausedWhatsApps.map(w => w.name).join(", ")}</strong>.{" "}
            {i18n.t("connectionBanner.pausedSuffix")}
          </span>
          <button
            onClick={() => history.push("/connections")}
            style={{
              background: "rgba(0,0,0,0.35)",
              border: "none",
              color: "#fff",
              padding: isSmall ? "2px 8px" : "3px 12px",
              borderRadius: 4,
              cursor: "pointer",
              fontWeight: "bold",
              fontSize: isSmall ? 10 : 12,
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {i18n.t("connectionBanner.goToConnections")}
          </button>
        </div>
      )}

      <Drawer
        variant={drawerVariant}
        className={drawerOpen ? classes.drawerPaper : classes.drawerPaperClose}
        classes={{
          paper: clsx(
            classes.drawerPaper,
            !drawerOpen && classes.drawerPaperClose
          ),
        }}
        open={drawerOpen}
        onMouseEnter={handleDrawerMouseEnter}
        onMouseLeave={handleDrawerMouseLeave}
      >
        <div
          className={classes.toolbarIcon}
          style={drawerOpen ? { justifyContent: "space-between", padding: "8px 8px 8px 16px", minHeight: 56 } : {}}
        >
          {drawerOpen && <img src={getWLLogo(whiteLabel, darkMode)} alt={whiteLabel?.name || "Logo"} style={{ height: 36 }} />}
          <div style={{ display: "flex", alignItems: "center" }}>
            {drawerVariant === "permanent" && drawerOpen && (
              <div className={classes.drawerHeaderDivider} />
            )}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <IconButton
                size="small"
                onClick={() => setDrawerOpen(!drawerOpen)}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              {drawerVariant === "permanent" && drawerOpen && (
                <Tooltip
                  title={
                    sidebarPinned
                      ? i18n.t("mainDrawer.sidebar.unpin")
                      : i18n.t("mainDrawer.sidebar.pin")
                  }
                  placement="right"
                >
                  <IconButton
                    size="small"
                    onClick={() => setSidebarPinned(p => !p)}
                  >
                    {sidebarPinned ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
        <Divider />
        <List>
          <MainListItems drawerClose={drawerClose} drawerOpen={drawerOpen} />
        </List>
        <Divider />
      </Drawer>
      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        // feat_299: "Perfil" edita SIEMPRE la cuenta del admin real, nunca la
        // del usuario que se está viendo (es un formulario de escritura).
        userId={realUser?.id ?? user?.id}
      />
      {/* feat_299 — selector de "Ver como usuario" */}
      <ViewAsUserModal
        open={viewAsDialogOpen}
        onClose={() => setViewAsDialogOpen(false)}
        onSelect={activateViewAs}
      />
      <AppBar
        position="absolute"
        className={clsx(
          classes.appBar,
          drawerOpen && classes.appBarShift
        )}
      >
        <Toolbar variant="dense" className={classes.toolbar}>
          <IconButton
            edge="start"
            aria-label="open drawer"
            onClick={() => setDrawerOpen(!drawerOpen)}
            className={clsx(
              classes.menuButton,
              drawerOpen && classes.menuButtonHidden
            )}
          >
            <MenuIcon />
          </IconButton>
          <div className={classes.title}>
            {!isSmall && <AppBanner showLogo={!drawerOpen} />}
          </div>

          {!isSmall && !user.isSuperAdmin && user.profile !== "profesional" && user.profile !== "agendador" && summaryStats && (
            <HeaderMetrics summaryStats={summaryStats} theme={theme} />
          )}

          {/* feat_288: switch operativo IA en toolbar (desktop, admin con iaAdminCanToggle) */}
          {!isSmall && showIaMenu && (
            <Tooltip
              title={
                (secretariaIaActive ? i18n.t("ia.toolbarActiveTooltip") : i18n.t("ia.toolbarInactiveTooltip")) +
                // todo_097: con líneas de los dos tipos, decir A QUIÉN gobierna.
                (iaToolbarMixed ? ` ${i18n.t("ia.toolbarTurnosNote")}` : "")
              }
              arrow
            >
              <Box style={{ display: "flex", alignItems: "center", marginRight: 8 }}>
                <Typography variant="caption" style={{ marginRight: 4, color: "inherit" }}>🤖 IA</Typography>
                <Switch
                  size="small"
                  checked={secretariaIaActive}
                  onChange={(e) => handleToggleSecretariaIaMenu(e.target.checked)}
                  color="default"
                  style={{ color: secretariaIaActive ? "#25D366" : "inherit" }}
                />
              </Box>
            </Tooltip>
          )}

          {user.id && !user.isSuperAdmin && user.profile !== "profesional" && user.profile !== "agendador" && (
            <NotificationsPopOver className={classes.iconButton} />
          )}

          {/* feat_316: preferencias de notificación del propio usuario.
              Mismo gate que NotificationsPopOver — solo donde suena la campanita. */}
          {user.id && !user.isSuperAdmin && user.profile !== "profesional" && user.profile !== "agendador" && (
            <NotificationSettingsButton />
          )}

          {/* feat_236: bell de notificaciones de creación de empresa (solo super superadmin) */}
          <NotificationBell />

          <div style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
            {user?.id && (
              <Typography className={classes.userName}>{getDisplayName()}</Typography>
            )}
            <IconButton
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              className={classes.iconButton}
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              getContentAnchorEl={null}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={menuOpen}
              onClose={handleCloseMenu}
            >
              <MenuItem onClick={toggleShowGroups}>
                <span style={{ flex: 1, marginRight: 16 }}>Grupos</span>
                <Switch
                  size="small"
                  checked={showGroups}
                  onChange={toggleShowGroups}
                  onClick={(e) => e.stopPropagation()}
                  color="primary"
                />
              </MenuItem>
              <MenuItem onClick={toggleHideChannels}>
                <span style={{ flex: 1, marginRight: 16 }}>Canales</span>
                <Switch
                  size="small"
                  checked={!hideChannels}
                  onChange={toggleHideChannels}
                  onClick={(e) => e.stopPropagation()}
                  color="primary"
                />
              </MenuItem>
              <Divider />
              <MenuItem onClick={toggleTheme}>
                {darkMode
                  ? <Brightness7Icon fontSize="small" style={{ marginRight: 12 }} />
                  : <Brightness4Icon fontSize="small" style={{ marginRight: 12 }} />}
                <span style={{ flex: 1, marginRight: 16 }}>
                  {i18n.t("mainDrawer.appBar.user.darkMode")}
                </span>
                <Switch
                  size="small"
                  checked={darkMode}
                  onChange={toggleTheme}
                  onClick={(e) => e.stopPropagation()}
                  color="primary"
                />
              </MenuItem>
              {/* Densidad visual — feat_328 */}
              <MenuItem disableRipple style={{ cursor: "default" }}>
                <span style={{ flex: 1, marginRight: 8, fontSize: 13 }}>
                  {i18n.t("mainDrawer.appBar.user.density")}
                </span>
                <div
                  style={{ display: "flex", gap: 4 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {[
                    { value: "compact", label: i18n.t("mainDrawer.appBar.user.densityCompact") },
                    { value: "normal", label: i18n.t("mainDrawer.appBar.user.densityNormal") },
                    { value: "comfortable", label: i18n.t("mainDrawer.appBar.user.densityComfortable") },
                  ].map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => setDensityValue(value)}
                      style={{
                        fontSize: 11,
                        padding: "2px 8px",
                        borderRadius: 10,
                        border:
                          density === value
                            ? "1.5px solid #2576d2"
                            : "1px solid rgba(0,0,0,0.23)",
                        backgroundColor:
                          density === value ? "#2576d222" : "transparent",
                        color: density === value ? "#2576d2" : "inherit",
                        cursor: "pointer",
                        fontWeight: density === value ? 700 : 400,
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </MenuItem>
              {showIaMenu && (
                <>
                  <Divider />
                  <MenuItem disabled style={{ opacity: 0.8, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>
                    🤖 {i18n.t("settings.iaControlSectionTitle")}
                  </MenuItem>
                  <MenuItem onClick={() => handleToggleSecretariaIaMenu(!secretariaIaActive)}>
                    <span style={{ flex: 1, marginRight: 16 }}>{i18n.t("ia.menuSecretariaLabel")}</span>
                    <Switch
                      size="small"
                      checked={secretariaIaActive}
                      onChange={(e) => handleToggleSecretariaIaMenu(e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                      color="primary"
                    />
                  </MenuItem>
                  <Tooltip title={!secretariaIaActive ? i18n.t("ia.vendedorDisabledTooltip") : ""} arrow placement="left">
                    <MenuItem
                      onClick={() => { if (secretariaIaActive) handleToggleVendedorIaMenu(!vendedorIaActive); }}
                      style={!secretariaIaActive ? { opacity: 0.5 } : {}}
                    >
                      <span style={{ flex: 1, marginRight: 16 }}>{i18n.t("ia.menuVendedorLabel")}</span>
                      <Switch
                        size="small"
                        disabled={!secretariaIaActive}
                        checked={vendedorIaActive && secretariaIaActive}
                        onChange={(e) => handleToggleVendedorIaMenu(e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                        color="primary"
                      />
                    </MenuItem>
                  </Tooltip>
                </>
              )}
              <Divider />
              {/* feat_299: solo admin de empresa (el superadmin usa la
                  impersonación real de feat_233) y solo si NO está ya en modo
                  view-as — no se anida. Gate por realUser: en modo view-as el
                  `user` del contexto ya es el target (no-admin). */}
              {realUser?.profile === "admin" && !realUser?.isSuperAdmin && !viewAsUser && (
                <MenuItem onClick={() => { setViewAsDialogOpen(true); handleCloseMenu(); }}>
                  {i18n.t("viewAs.menuLabel")}
                </MenuItem>
              )}
              <MenuItem onClick={handleOpenUserModal}>
                {i18n.t("mainDrawer.appBar.user.profile")}
              </MenuItem>
              <MenuItem onClick={handleClickLogout}>
                {i18n.t("mainDrawer.appBar.user.logout")}
              </MenuItem>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>
      <main className={classes.content}>
        <div style={{ minHeight: 48 }} />
        <MatchPropBanner />
        {children ? children : null}
      </main>
      <FloatingChat />
    </div>
    </BannerActiveProvider>
    </ChatHighlightProvider>
  );
};

export default LoggedInLayout;
